import React, {useMemo} from 'react';
import {Cell, Legend, Pie, PieChart, ResponsiveContainer, Tooltip} from 'recharts';
import {CHART_COLORS} from './chartUtils';
import {formatShare} from '../data_explorer/channelApplicability';

/** El % de una porción, o `null` sin `share` (el motor no lo calcula para tasas como CPA o CTR). */
export function sliceShareText(share, locale = 'es') {
    return typeof share === 'number' && Number.isFinite(share) ? formatShare(share, locale) : null;
}

/**
 * Torta genérica. Recibe las filas YA agrupadas — no sabe de `results` ni de `chartPrefs`: eso es
 * del caller. Misma paleta que los otros charts.
 *
 * ⚠️ El nombre dice «Shared» y el docstring decía «Explorador / Informes», pero **hoy su único
 * consumidor es Informes** (`panels/PanelBodies.js`): el Explorador dibuja su propia torta, con su
 * propio recorte de etiquetas. Verificado por grep (review del 08/09). Igual se lo trata como
 * compartido —está en `components/charts/` y cualquiera puede tomarlo— así que todo agregado va
 * como opción, nunca cambiando lo que ya hace.
 *
 * Props:
 *  - rows: Array<{label, value, formatted_value?, share?}>
 *  - height: number (default 300)
 *  - colors: string[] (default CHART_COLORS)
 *  - showLegend: boolean (default true)
 *  - legendLabelMaxChars: number (opcional) — techo de ancho de la etiqueta de la leyenda, en
 *    caracteres. Sin la prop, la leyenda se comporta exactamente como siempre. La usa Informes,
 *    donde una dimensión de URLs desbordaba la tarjeta por encima de su propio selector.
 *
 *    ⚠️ Recorta por **CSS**, no cortando el string: así el nombre COMPLETO queda en el DOM y en el
 *    `title`. Con el recorte en JS, dos campañas que empiezan igual se truncaban al MISMO texto y la
 *    leyenda quedaba literalmente indescifrable — no se podía saber qué porción era cuál
 *    (QA-editor-07). Es además lo que hace el mockup.
 *  - showShare: boolean (default false) — suma el % de cada porción al tooltip y a la leyenda
 *    («$14,853.51 (98 %)»), con el `share` que calcula el motor. Lo pidió iVirtual (wa#220): reparten
 *    la inversión entre Google y Meta y la torta no decía la proporción. Sin `share` no muestra nada.
 *  - locale: string (default 'es') — formato del %.
 */
export function SharedPieChart({rows, height = 300, colors = CHART_COLORS, showLegend = true,
                                legendLabelMaxChars = null, showShare = false, locale = 'es'}) {
    const data = useMemo(
        () => (rows || []).filter((r) => r && typeof r.value === 'number' && r.value > 0)
            .map((r) => ({name: r.label, value: r.value, formatted: r.formatted_value, share: r.share})),
        [rows]
    );
    const shareByName = useMemo(() => new Map(data.map((d) => [d.name, d.share])), [data]);
    if (!data.length) return null;

    const shareOf = (share) => (showShare ? sliceShareText(share, locale) : null);
    const legendFormatter = (legendLabelMaxChars || showShare)
        ? (value) => {
            const label = legendLabelMaxChars
                ? (
                    <span className="shared-legend-label" title={value}
                          style={{maxWidth: `${legendLabelMaxChars}ch`}}>{value}</span>
                )
                : value;
            const share = shareOf(shareByName.get(value));
            return share ? <>{label} <span className="shared-legend-share">{share}</span></> : label;
        }
        : undefined;

    return (
        <ResponsiveContainer width="100%" height={height}>
            <PieChart>
                <Pie data={data} dataKey="value" nameKey="name" innerRadius="0%" outerRadius="80%" paddingAngle={1}>
                    {data.map((entry, index) => (
                        <Cell key={entry.name} fill={colors[index % colors.length]}/>
                    ))}
                </Pie>
                <Tooltip formatter={(value, name, item) => {
                    const payload = item && item.payload;
                    const shown = payload && payload.formatted ? payload.formatted : value;
                    const share = payload ? shareOf(payload.share) : null;
                    return [share ? `${shown} (${share})` : shown, name];
                }}/>
                {showLegend && (
                    <Legend layout="vertical" align="right" verticalAlign="middle" formatter={legendFormatter}/>
                )}
            </PieChart>
        </ResponsiveContainer>
    );
}
